/* eslint-disable no-underscore-dangle,no-use-before-define,max-len,no-param-reassign */
import { i18n } from 'vue-infra';
import { get, isNil, pickBy, includes, isEmpty, isUndefined, some, isNull } from 'lodash';
import { ROOT_GETTER_PATHS, INFRA_GETTERS } from '@core/store';
import {
  DATA_LOADING_STATES,
  WIDGET_LOGICAL_NAMES,
  METRIC_NAMES,
  FEATURES,
} from '@config/Consts';
import { ALERT_CONFIG } from '@config/AlertConfig';
import { metricsConfig as METRIC_DETAILS_PER_WIDGET } from '@config/metricsConfig';
import {
  getLastTimeUpdated,
  getMetricScoreWithUnit,
  metricAlertData,
  getDialogAlertItemsByWidget,
  sendThresholdEvent
} from '@core/utils/utils';
import AlertModel from '@core/models/AlertModel';

const _getMetricsConfiguration = (state, getters, rootState, rootGetters) => {
  const metricGetter = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_WIDGET_VALID_METRICS];
  return metricGetter(WIDGET_LOGICAL_NAMES.SHIFT_STATUS);
};

const getTimeFrameFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_TIMEFRAME_FILTER];

const getUserIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_USERS];

const getHumanUserIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_HUMAN_USERS];

const getMetricDataLoading = state => metricName => state.metricsDataLoading[metricName];

const _getMetricLastTimeUpdated = state => metricName => state.lastTimeDataUpdated[metricName];

const getMetricData = state => metricName => state.metrics[metricName];

const _generateErrorTooltip = (lastTimeUpdated) => {
  let tooltip = i18n.t('amd:activity_summary_first_load_warning_tooltip');
  if (!isNull(lastTimeUpdated)) {
    tooltip = i18n.t('amd:activity_summary_warning_tooltip',
      { lastRefreshTime: getLastTimeUpdated(lastTimeUpdated) });
  }
  tooltip += ` ${i18n.t('amd:obsolete_warning_suffix_tooltip')}`;
  return tooltip;
};

const getMetricErrorTooltip = (state, getters) => (metricName) => {
  const loadingStatus = getters.getMetricDataLoading(metricName);
  const lastTimeUpdated = _getMetricLastTimeUpdated(state)(metricName);
  const showError = loadingStatus === DATA_LOADING_STATES.ERROR
    || loadingStatus === DATA_LOADING_STATES.WRONG_FORMAT;
  return showError ? _generateErrorTooltip(lastTimeUpdated) : undefined;
};

const getMetricDisplayedScore = getters => (metricName, score, unitType) => {
  let displayedScore = '--';
  const loadingStatus = getters.getMetricDataLoading(metricName);
  // display N/A if metric is in error state on first time load
  if (loadingStatus === DATA_LOADING_STATES.ERROR && isNull(score)) {
    displayedScore = i18n.t('amd:missing_value_na');
  } else if (!isNil(score)) {
    displayedScore = getMetricScoreWithUnit(score, unitType);
  }
  return displayedScore;
};

const getFilteredGroupsIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GROUPS_FILTER];

const getSkillsFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_SKILLS_IDS];

const getFirstTimeLoading = state => state.firstTimeLoading;

const isWaitingForData = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.LOADING ||
  getFirstTimeLoading(state);

const getAlertData = (state, getters, rootState, rootGetters) => {
  const allAlerts = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ALERT_DATA];
  return get(allAlerts, WIDGET_LOGICAL_NAMES.SHIFT_STATUS) || {};
};

const _getExtensionMetrics = (state, getters, rootState, rootGetters,
  extensionMetrics, hideExtensionMetricByUseNewManagerApiFlag) => {
  let result = extensionMetrics;
  if (hideExtensionMetricByUseNewManagerApiFlag) {
    result = getUseNewManagerApi(state, getters, rootState, rootGetters) ? extensionMetrics : [];
  }
  return result;
};

const getDialogAlertItems = (getters, metrics) =>
  getDialogAlertItemsByWidget(metrics, WIDGET_LOGICAL_NAMES.SHIFT_STATUS, getters.getAlertData);

const getThresholdEventOptions = (metricName, metricScore, alertData) => {
  const settings = {
    widgetName: WIDGET_LOGICAL_NAMES.SHIFT_STATUS,
    metricName,
    metricScore,
    alertData,
  };
  return settings;
};

const getMetricsMap = (state, getters, rootState, rootGetters) => {
  const metrics = _getMetricsConfiguration(state, getters, rootState, rootGetters);
  const metricScores = state.metrics;
  const widgetMetricsMap = METRIC_DETAILS_PER_WIDGET[WIDGET_LOGICAL_NAMES.SHIFT_STATUS];
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];

  return metrics.reduce((final, metric) => {
    if (metric.config.enabled) {
      const metricDetails = get(widgetMetricsMap, [metric.name]);

      const {
        metricLabelI18nKey,
        metricName: metricLogicalName,
        metricTooltipI18nKey,
        metricUnitType,
      } = metricDetails;

      if (!isNil(metricLogicalName) && !isNil(metricDetails)) {
        const name = i18n.t(`amd:${metricLabelI18nKey}`);
        const metricAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.SHIFT_STATUS}.${metricLogicalName}`);
        const showSetAlert = alertPermissions.canSetAlert && !!get(metricAlertConfig, 'showAlertConfiguration');
        const extensionMetrics = _getExtensionMetrics(
          state, getters, rootState, rootGetters,
          metricDetails.extensionMetrics,
          metricDetails.hideExtensionMetricByUseNewManagerApiFlag,
        );
        const allMetrics = isUndefined(extensionMetrics) ? [metricLogicalName] : [metricLogicalName, ...extensionMetrics];
        const allMetricAlerts = pickBy(getters.getAlertData, (value, key) => includes(allMetrics, key));

        let [isAlertSet, showAlert, dialogAlertItems, alertTooltip, alertModel] = [false, false, undefined, '', null];
        isAlertSet = some(allMetricAlerts, metricAlert => !isEmpty(get(metricAlert, 'conditionType')));
        dialogAlertItems = getDialogAlertItems(getters, allMetrics);

        if (alertPermissions.canViewAlert && isAlertSet) {
          const metricScore = metricScores[metricLogicalName];
          const currentMetric = get(allMetricAlerts, metricLogicalName);
          if (currentMetric) {
            const metricAlert = metricAlertData(currentMetric, metricScore, metricAlertConfig.validation, name);
            showAlert = metricAlert.showAlert;
            alertTooltip = metricAlert.tooltip;
            if (showAlert) {
              sendThresholdEvent(getThresholdEventOptions(metricLogicalName, metricScore, get(getters.getAlertData, metricLogicalName)));
              alertModel = new AlertModel({
                origin: `${WIDGET_LOGICAL_NAMES.SHIFT_STATUS}_${metric.name}`,
                message: alertTooltip,
                showAlert: true
              });
            } else {
              alertModel = new AlertModel({
                origin: `${WIDGET_LOGICAL_NAMES.SHIFT_STATUS}_${metric.name}`,
                message: alertTooltip,
                showAlert: false
              });
            }
          }
        }
        final[metricLogicalName] = {
          name,
          className: name.replace(/ /g, '_'),
          logicalName: metricLogicalName,
          score: metricScores[metricLogicalName],
          description: i18n.t(`amd:${metricTooltipI18nKey}`),
          unitType: metricUnitType,
          extensionMetrics,
          display: !metric.config.parentMetric,
          displayOrder: metric.config.displayOrder,
          longMetricName: metricLogicalName === METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE,
          errorTooltip: getMetricErrorTooltip(state, getters)(metricLogicalName),
          displayedScore: getMetricDisplayedScore(getters)(metricLogicalName, metricScores[metricLogicalName], metricUnitType),
          showSetAlert,
          isAlertSet,
          alertModel,
          showAlert,
          dialogAlertItems,
          alertTooltip,
          widgetName: WIDGET_LOGICAL_NAMES.SHIFT_STATUS,
        };
      }
    }
    return final;
  }, {});
};

const getUseNewManagerApi = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_USE_NEW_MANAGER_API];

const getFetchResponseMetricsState = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FETCH_RESPONSE_METRICS];

const _allMetricsAreInErrorState = (state, getters, rootState, rootGetters) => {
  const metrics = getMetricsMap(state, getters, rootState, rootGetters);
  let isInErrorState = true;
  Object.values(metrics).forEach((metric) => {
    isInErrorState = isInErrorState && getters.getMetricDataLoading(metric.logicalName)
      === DATA_LOADING_STATES.ERROR;
  });
  return isInErrorState;
};

const _isEmptyMetricsData = (state, getters, rootState, rootGetters) => {
  const metrics = getMetricsMap(state, getters, rootState, rootGetters);
  const metricsAreNotFetched = some(Object.values(metrics), metric => isNull(getMetricData(state)(metric.logicalName)));
  return metricsAreNotFetched;
};

// TODO: check merge if need to delete this
const showOutOfDateError = (state, getters) => {
  const metrics = getters.getMetricsMap;
  const atLeastOneMetricIsFetched = some(metrics, metric =>
    !isNull(getMetricData(state)(metric.logicalName)));
  const atLeastOneMetricInErrorState = some(metrics, (metric) => {
    const status = getMetricDataLoading(state)(metric.logicalName);
    return status === DATA_LOADING_STATES.ERROR;
  });
  return atLeastOneMetricIsFetched && atLeastOneMetricInErrorState;
};

/* The widget loading error is displayed in the following case:
  when all the metrics return error at first load
*/
const displayWidgetError = (state, getters, rootState, rootGetters) => {
  const errorState = DATA_LOADING_STATES.ERROR;
  const result = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === errorState
    || (
      _isEmptyMetricsData(state, getters, rootState, rootGetters) &&
      _allMetricsAreInErrorState(state, getters, rootState, rootGetters)
    );
  return result;
};

const hasContactCenterFeature = (state, getters, rootState, rootGetters) =>
  rootGetters[INFRA_GETTERS.FEATURE_STATE](FEATURES.CONTACT_CENTER);

export default {
  getTimeFrameFilter,
  getUserIds,
  getHumanUserIds,
  getFilteredGroupsIds,
  getMetricDataLoading,
  getFirstTimeLoading,
  getUseNewManagerApi,
  getSkillsFilter,
  getFetchResponseMetricsState,
  isWaitingForData,
  displayWidgetError,
  getMetricData,
  getAlertData,
  hasContactCenterFeature,
  showOutOfDateError,
  //
  getMetricsMap,
};
